import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BACKEND_URL from "../config/config";
import {
  getRuntimeFeatureFlags,
  persistFeatureFlags,
  resolveFeatureFlags,
} from "../config/featureFlags";
import { showPopup } from "../utils/popup";

function useEmissionForm(language = "ES") {
  const getStoredValue = (key, defaultValue = "") =>
    sessionStorage.getItem(key) || defaultValue;

  const setStoredValue = (key, value) => {
    sessionStorage.setItem(key, value);
  };

  const getEmissionValue = (data, keys) => {
    for (const key of keys) {
      const value = key
        .split(".")
        .reduce((current, part) => current?.[part], data);
      const number = Number(value);

      if (Number.isFinite(number)) return number;
    }

    return null;
  };

  const persistEmissionBreakdown = (data) => {
    const air = getEmissionValue(data, [
      "co2_air_user",
      "co2_air",
      "air_emission",
      "air_emissions",
      "emissions.co2_air_user",
      "emissions.air",
    ]);
    const land = getEmissionValue(data, [
      "co2_land_user",
      "co2_transport_user",
      "co2_land",
      "land_emission",
      "land_emissions",
      "emissions.co2_land_user",
      "emissions.land",
    ]);
    const hotel = getEmissionValue(data, [
      "co2_hotel_user",
      "co2_hotel",
      "hotel_emission",
      "hotel_emissions",
      "emissions.co2_hotel_user",
      "emissions.hotel",
    ]);

    if (air !== null) sessionStorage.setItem("co2_air_user", String(air));
    if (land !== null) sessionStorage.setItem("co2_land_user", String(land));
    if (hotel !== null) sessionStorage.setItem("co2_hotel_user", String(hotel));
  };

  const storedAirTravel = getStoredValue("air_travel");
  const storedTravelTime = getStoredValue("travel_time");
  const storedHotelFlag = getStoredValue("stayed_in_hotel");
  const storedHotelNights = getStoredValue("hotel_nights");

  const [airTravel, setAirTravel] = useState(storedAirTravel);
  const [travelTime, setTravelTime] = useState(
    storedAirTravel === "0" && storedTravelTime === "0" ? "" : storedTravelTime
  );
  const [eventTransport, setEventTransport] = useState(
    getStoredValue("event_transport")
  );
  const [kmLand, setKmLand] = useState(getStoredValue("km_land"));
  const [stayedInHotelFlag, setStayedInHotelFlag] = useState(storedHotelFlag);
  const [hotelNights, setHotelNights] = useState(
    storedHotelFlag === "0" && storedHotelNights === "0"
      ? ""
      : storedHotelNights
  );

  const [questionsEmissions1, setQuestionsEmissions1] = useState(
    getStoredValue("questions_emissions_1")
  );
  const [questionsEmissions2, setQuestionsEmissions2] = useState(
    getStoredValue("questions_emissions_2")
  );
  const [questionsEmissions3, setQuestionsEmissions3] = useState(
    getStoredValue("questions_emissions_3")
  );

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runtimeFlags, setRuntimeFlags] = useState(getRuntimeFeatureFlags);

  const isHotelYes = stayedInHotelFlag === "1";
  const requiresScope =
    questionsEmissions1 === "Sí" || questionsEmissions1 === "En proceso";

  const navigate = useNavigate();
  const { IS_FREE, ENABLE_QUESTIONS } = runtimeFlags;

  useEffect(() => {
    let cancelled = false;

    const loadPublicConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/public-config`);
        if (!res.ok) throw new Error("No se pudo cargar public-config");

        const data = await res.json();
        if (cancelled) return;

        persistFeatureFlags(data);
        setRuntimeFlags(resolveFeatureFlags(data));
      } catch (error) {
        console.error("Error cargando public-config:", error);
      }
    };

    loadPublicConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  const btnBack = () => {
    navigate("/registration");
  };

  const btnNext = async (e) => {
    e.preventDefault();

    const errors = {};
    const hasPositiveNumber = (value) => {
      const number = Number(value);
      return Number.isFinite(number) && number > 0;
    };

    if (!airTravel) errors.airTravel = "Debe seleccionar una opción de vuelo";
    if (airTravel === "1" && !hasPositiveNumber(travelTime)) {
      errors.travelTime = "Debe seleccionar una cantidad de horas";
    }

    if (!eventTransport) {
      errors.eventTransport = "Debe seleccionar un tipo de transporte";
    }
    if (!kmLand) errors.kmLand = "Debe seleccionar una cantidad de km";

    if (!stayedInHotelFlag) {
      errors.stayedInHotelFlag = "Debe seleccionar una opción de hotel";
    }
    if (stayedInHotelFlag === "1" && !hasPositiveNumber(hotelNights)) {
      errors.hotelNights = "Debe seleccionar una cantidad de noches";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (isSubmitting) return;
    setIsSubmitting(true);

    const codeIsValid = sessionStorage.getItem("code_is_valid") === "true";
    const shouldPay = !IS_FREE && !codeIsValid;

    try {
      const pick = (key, defaultValue = "") =>
        sessionStorage.getItem(key) || defaultValue;

      const toNum = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const hotelFlag = toNum(pick("stayed_in_hotel"));
      const nights = toNum(pick("hotel_nights"));

      const payload = {
        first_name: pick("first_name"),
        last_name: pick("last_name"),
        dni: pick("dni"),
        email: pick("email"),
        phone: pick("phone"),
        position: pick("position"),
        region: pick("region"),
        sector: pick("sector"),
        employees: pick("employees"),
        id_company: pick("id_company")
          ? parseInt(pick("id_company"), 10)
          : null,
        company_name: pick("company_name") || null,
        invitation_code:
          !IS_FREE && codeIsValid
            ? (pick("invitation_code") || "").toUpperCase().trim()
            : "",
        panel_topics: pick("panel_topics"),
        air_travel: pick("air_travel"),
        travel_time: toNum(pick("travel_time")),
        event_transport: pick("event_transport"),
        km_land: toNum(pick("km_land")),
        stayed_in_hotel: hotelFlag,
        hotel_nights: hotelFlag ? nights : 0,
        extra_answers: {
          questions_emissions_1: pick("questions_emissions_1"),
          questions_emissions_2: pick("questions_emissions_2"),
          questions_emissions_3: pick("questions_emissions_3"),
        },
        inscripto: false,
      };

      const res = await fetch(`${BACKEND_URL}/responses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.status === "error") {
        const msg =
          data?.code ||
          data?.error ||
          "No pudimos guardar tu inscripción. Probá nuevamente.";
        showPopup(msg, {
          title: "No pudimos guardar tu inscripción",
          type: "error",
        });
        return;
      }

      const co2 =
        data?.co2_total_per_user ??
        data?.co2_emitted_user ??
        data?.emissions_kg_co2e;
      const co2Number = Number(co2);

      if (Number.isFinite(co2Number)) {
        localStorage.setItem("emissions_kg_co2e", String(co2Number));
        sessionStorage.setItem("co2_total_per_user", String(co2Number));
      }

      persistEmissionBreakdown(data);

      if (data?.user_id) {
        localStorage.setItem("user_id", String(data.user_id));
        sessionStorage.setItem("user_id", String(data.user_id));
      }

      const email = (pick("email") || "").trim();
      if (email) localStorage.setItem("email", email);
      localStorage.setItem("code_is_valid", codeIsValid ? "true" : "false");

      if (ENABLE_QUESTIONS && !IS_FREE) {
        navigate("/questions");
        return;
      }

      if (shouldPay) {
        navigate("/summary");
        return;
      }

      navigate("/thanks");
    } catch (err) {
      console.error(err);
      showPopup("Probá nuevamente en unos minutos.", {
        title: "No pudimos guardar tu inscripción",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventTransports = [
    "Auto",
    "Auto compartido",
    "Camioneta",
    "Camioneta compartida",
    "Colectivo",
    "Subte",
    "Ferry",
    "Bicicleta",
    "Caminando",
    "Moto",
    "Auto híbrido",
    "Auto eléctrico",
  ];

  const transportLabels = {
    EN: {
      Auto: "Car",
      "Auto compartido": "Shared car",
      Camioneta: "Pickup truck",
      "Camioneta compartida": "Shared pickup truck",
      Colectivo: "Bus",
      Subte: "Subway",
      Ferry: "Ferry",
      Bicicleta: "Bicycle",
      Caminando: "Walking",
      Moto: "Motorcycle",
    },
    ES: {},
  };

  return {
    airTravel,
    setAirTravel,
    travelTime,
    setTravelTime,
    eventTransport,
    setEventTransport,
    kmLand,
    setKmLand,
    stayedInHotelFlag,
    setStayedInHotelFlag,
    hotelNights,
    setHotelNights,
    isHotelYes,
    questionsEmissions1,
    setQuestionsEmissions1,
    questionsEmissions2,
    setQuestionsEmissions2,
    questionsEmissions3,
    setQuestionsEmissions3,
    formErrors,
    requiresScope,
    isSubmitting,
    setStoredValue,
    btnBack,
    btnNext,
    eventTransports: eventTransports.map((value) => ({
      value,
      label: transportLabels[language]?.[value] || value,
    })),
  };
}

export default useEmissionForm;
